import 'vuetify/styles';
import '../main.scss';
import '@mdi/font/css/materialdesignicons.min.css';
import { createVuetify, type DefaultsInstance, type ThemeDefinition } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import { EpubPlayerTheme } from '@/types/frontend';

const light: ThemeDefinition = {
    dark: false,
    colors: {
        primary: '#125E87',
        'on-primary': '#FFFFFF',
        'primary-container': '#C0E0F2',
        'on-primary-container': '#0D2E3F',
        secondary: '#325E76',
        'on-secondary': '#FFFFFF',
        'secondary-container': '#C9DDE8',
        'on-secondary-container': '#172B36',
        tertiary: '#883A7F',
        'on-tertiary': '#FFFFFF',
        'tertiary-container': '#E8C9E4',
        'on-tertiary-container': '#361732',
        error: '#B82E2E',
        'on-error': '#FFFFFF',
        'error-container': '#F0C2C2',
        'on-error-container': '#3D0F0F',
        surface: '#F9FBFC',
        'on-surface': '#1D1F20',
        'on-surface-medium': '#494E50',
        outline: '#798186',
        'outline-variant': '#C9CDCF',
        shadow: '#000000',
        scrim: '#000000',
        'inverse-surface': '#1F3947',
        'inverse-on-surface': '#F1F2F3',
        'inverse-primary': '#A5D8F3',
        'primary-fixed': '#D0EBF9',
        'on-primary-fixed': '#0C3F5A',
        'primary-fixed-dim': '#A5D8F3',
        'on-primary-fixed-variant': '#125E87',
        'secondary-fixed': '#DAE8EF',
        'on-secondary-fixed': '#1F3947',
        'secondary-fixed-dim': '#B8D2E0',
        'on-secondary-fixed-variant': '#2E566B',
        'tertiary-fixed': '#F0DBED',
        'on-tertiary-fixed': '#471F43',
        'tertiary-fixed-dim': '#E0B8DC',
        'on-tertiary-fixed-variant': '#6B2E64',
        'surface-dim': '#C6DBE7',
        'surface-bright': '#FBFDFD',
        'surface-container-lowest': '#FFFFFF',
        'surface-container-low': '#F1F6F9',
        'surface-container': '#E6EFF4',
        'surface-container-high': '#DBE9F0',
        'surface-container-highest': '#D4E4ED',
        brand: '#177DB5',
        'on-brand': '#FFFFFF',
        accent: '#B24DA7',
        'on-accent': '#FFFFFF',
        'on-surface-low': '#949B9E',
        orange: '#F7893F',
        'on-orange': '#FFFFFF',
        'orange-accent': '#EC6A13',
        'on-orange-accent': '#FFFFFF',
        'orange-high': '#8E3F0B',
        'orange-medium': '#BD550F',
        teal: '#79CCC8',
        'on-teal': '#FFFFFF',
        'teal-accent': '#3EA39E',
        'on-teal-accent': '#FFFFFF',

        'hots-remember': '#B05CD6',
        'hots-remember-surface': '#EFDEF7',
        'hots-on-remember-surface': '#8D2EB7',
        'hots-understand': '#864AD4',
        'hots-understand-surface': '#E9DEF7',
        'hots-on-understand-surface': '#6A2CB9',
        'hots-apply': '#5555E7',
        'hots-apply-surface': '#DBDBFA',
        'hots-on-apply-surface': '#3636E3',
        'hots-analyse': '#41A428',
        'hots-analyse-surface': '#E3F7DE',
        'hots-on-analyse-surface': '#317B1E',
        'hots-evaluate': '#E96E16',
        'hots-evaluate-surface': '#FBE8DA',
        'hots-on-evaluate-surface': '#A84F10',
        'hots-create': '#E46768',
        'hots-create-surface': '#F9DCDD',
        'hots-on-create-surface': '#C32324',

        // TODO revise the colors below. Not part of new theme spec but used in the old one
        success: '#4CAF50',
        info: '#2196F3',
        warning: '#FB8C00',
        header: '#263033',
        grey: '#60787f',
        'panel-banner': '#dce3e5',
        'highlight-lighten': '#ebdcff',
        'on-highlight-lighten': '#362B4A',
        'secondary-lighten': '#ffdbc8',
        'primary-lighten': '#cae6ff',
        'on-secondary-lighten': '#321300',
        'on-primary-lighten': '#001E30',
    },
};
// on-surface: text color
// surface-container-lowest: background of pages
// surface: background outside of pages

export const epubPlayerThemes: EpubPlayerTheme[] = [
    {
        theme: 'white',
        name: 'White',
        colors: { 'surface-container-lowest': '#ffffff', surface: '#ffffff', 'on-surface': '#000000' },
    },
    {
        theme: 'ivory',
        name: 'Ivory',
        colors: { 'surface-container-lowest': '#f2f2f2', surface: '#f2f2f2', 'on-surface': '#000000' },
    },
    {
        theme: 'grey',
        name: 'Grey',
        colors: { 'surface-container-lowest': '#d8d8d8', surface: '#d8d8d8', 'on-surface': '#000000' },
    },
    {
        theme: 'blue',
        name: 'Blue',
        colors: { 'surface-container-lowest': '#ceecf5', surface: '#ceecf5', 'on-surface': '#000000' },
    },
    {
        theme: 'pink',
        name: 'Pink',
        colors: { 'surface-container-lowest': '#f6cece', surface: '#f6cece', 'on-surface': '#000000' },
    },
    {
        theme: 'yellow',
        name: 'Yellow',
        colors: { 'surface-container-lowest': '#fee2a9', surface: '#fee2a9', 'on-surface': '#000000' },
    },
    {
        theme: 'sepia',
        name: 'Sepia',
        colors: { 'surface-container-lowest': '#f5ecce', surface: '#f5ecce', 'on-surface': '#000000' },
    },
    {
        theme: 'purple',
        name: 'Purple',
        colors: { 'surface-container-lowest': '#B34CA7', surface: '#B34CA7', 'on-surface': '#000000' },
    },
    {
        theme: 'black-white',
        name: 'Black / White',
        colors: { 'surface-container-lowest': '#000000', surface: '#000000', 'on-surface': '#ffffff' },
    },
    {
        theme: 'black-yellow',
        name: 'Black / Yellow',
        colors: { 'surface-container-lowest': '#000000', surface: '#000000', 'on-surface': '#ffff00' },
    },
];

const themes: Record<string, ThemeDefinition> = epubPlayerThemes.reduce(
    (acc: Record<string, ThemeDefinition>, theme) => {
        acc[`player-${theme.theme}`] = {
            dark: false,
            colors: {
                ...light.colors,
                ...theme.colors,
            },
        };
        return acc;
    },
    { light },
);

export const gigletsDefaults: DefaultsInstance = {
    VDataTable: {
        mobile: false,
    },
    VTextarea: {
        variant: 'outlined',
        color: 'primary',
    },
    VCard: {
        style: { 'border-radius': '16px' },
        class: ['pa-6'],
    },
    VBtn: {
        style: {
            'border-radius': '12px',
        },
        height: 40,
        class: ['text-none'],
        elevation: 0,
    },
    VChipGroup: {
        selectedClass: 'bg-brand',
        VChip: {
            variant: 'flat',
            class: ['bg-surface-container', 'rounded-lg'],
        },
    },
};

export default createVuetify({
    defaults: gigletsDefaults,
    icons: {
        defaultSet: 'mdi',
        aliases,
        sets: {
            mdi,
        },
    },
    theme: {
        defaultTheme: 'light',
        variations: {
            colors: ['grey'],
            lighten: 4,
            darken: 1,
        },
        themes,
    },
});
